/**
 * @packageDocumentation
 * @module      CloudApiErrorEvent.ts
 * @description A custom event that is fired when there is an error calling a Cloud API function.
 * @copyright   Copyright (c) 2021 by Corindus Inc., a Siemens Healthineers Company. All rights reserved.
 */
/**
 * A custom event that is fired when a user successfully logs in.
 */
export default class CloudApiErrorEvent extends CustomEvent {
    /**
     * Constructor.
     */
    constructor(operation, httpStatus, responseText) {
        super(CloudApiErrorEvent.eventName, {
            bubbles: true,
            composed: true,
            detail: "There was an error while attempting to " + operation + ". " +
                (httpStatus === undefined ?
                    "There was no response from the server." :
                    CloudApiErrorEvent.httpCodeToDescription(httpStatus) +
                        " (HTTP code " + httpStatus + ")." +
                        (!!responseText && httpStatus != 403 ? (" " + responseText) : ""))
        });
    }
    static httpCodeToDescription(code) {
        // This switch statement has all the codes we currently return from the server.
        // Add to it as necessary.
        switch (code) {
            case 200:
                return "The request processed OK, but there was a subsequent error, perhaps while parsing the response";
            case 400:
                return "The request was ill-formed, probably indicating a programming error";
            case 403:
                return "You do not have the required permission";
            case 404:
                return "The record(s) were not found";
            case 422:
                return "The request could not be processed";
            case 500:
                return "There was an internal server error";
            default:
                return "There was an unplanned-for error";
        }
    }
    /**
     * Dispatch a CloudApiErrorEvent after waiting on the promise for Response.text(), if the response is not null.
     * If the element is undefined, nothing happens.
     *
     * @param fromElement The element to dispatch from, or undefined to do nothing.
     * @param operation A description of the operation being performed. See the like-named parameter in the event itself.
     * @param response The Response object whose error was the motive for this event. Null means there
     * was no response from the server.
     * @param message Used when the response was OK (200) but there was an error decoding the response (e.g.,
     * parsing the JSON).
     */
    static dispatch(fromElement, operation, response, message) {
        if (fromElement === undefined) {
            return;
        }
        const promiseForText = (response === null ?
            Promise.resolve(undefined) :
            (typeof message !== "undefined") ?
                Promise.resolve(message) :
                response.text());
        promiseForText.then(text => {
            const event = new CloudApiErrorEvent(operation, (response === null || response === void 0 ? void 0 : response.status) || undefined, text);
            fromElement.dispatchEvent(event);
        }, () => {
            const event = new CloudApiErrorEvent(operation, (response === null || response === void 0 ? void 0 : response.status) || undefined, undefined);
            fromElement.dispatchEvent(event);
        });
    }
}
/** The name of the event. */
CloudApiErrorEvent.eventName = "cloudApiError";
//# sourceMappingURL=CloudApiErrorEvent.js.map